import { Phone, MapPin, Clock3, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { PrairieMap } from "../components/PrairieMap";
import { BUSINESS, SERVICE_AREAS } from "../lib/content";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get In Touch"
        title="Let's talk about"
        accent="your project."
        subtitle="Call us — North West Asphalt serves Alberta, Saskatchewan and Manitoba. No pressure, just honest answers and a clear written quote."
        testId="contact-hero"
      />

      {/* Quick contact strip */}
      <section
        className="bg-[#050505] border-b border-[#27272a] py-10 md:py-12"
        data-testid="contact-strip"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-0 md:divide-x md:divide-[#27272a]">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            className="group flex items-center gap-4 md:px-8 first:md:pl-0 hover:text-[#f59e0b] transition-colors"
            data-testid="strip-phone"
          >
            <div className="w-12 h-12 bg-[#18181b] border border-[#27272a] group-hover:border-[#f59e0b] flex items-center justify-center shrink-0 transition-colors">
              <Phone className="w-5 h-5 text-[#f59e0b]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                Call us
              </div>
              <div className="font-display font-bold text-base md:text-lg leading-tight">
                {BUSINESS.phone}
              </div>
              <div className="font-display font-bold text-base md:text-lg leading-tight text-[#a1a1aa] group-hover:text-[#f59e0b] transition-colors">
                {BUSINESS.phone2}
              </div>
            </div>
          </a>
          <div
            className="flex items-center gap-4 md:px-8"
            data-testid="strip-coverage"
          >
            <div className="w-12 h-12 bg-[#18181b] border border-[#27272a] flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-[#f59e0b]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                Coverage
              </div>
              <div className="font-display font-bold text-lg md:text-xl">
                AB · SK · MB
              </div>
            </div>
          </div>
          <div
            className="flex items-center gap-4 md:px-8 last:md:pr-0"
            data-testid="strip-hours"
          >
            <div className="w-12 h-12 bg-[#18181b] border border-[#27272a] flex items-center justify-center shrink-0">
              <Clock3 className="w-5 h-5 text-[#f59e0b]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                Open
              </div>
              <div className="font-display font-bold text-lg md:text-xl">
                Mon – Sat
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main grid */}
      <section className="py-20 md:py-28" data-testid="contact-main">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Left: Big phone */}
          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="label-sm text-[#f59e0b] mb-5">Direct line</div>
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="group inline-flex flex-col"
                data-testid="contact-phone"
              >
                <div className="font-display font-black text-5xl sm:text-7xl md:text-[6.5rem] uppercase leading-[0.85] tracking-tight group-hover:text-[#f59e0b] transition-colors">
                  {BUSINESS.phone}
                </div>
                <div className="flex items-center gap-3 mt-4 text-xs uppercase tracking-[0.3em] text-[#a1a1aa]">
                  <Phone className="w-4 h-4 text-[#f59e0b]" strokeWidth={3} />
                  Primary · Tap to call
                </div>
              </a>
            </div>

            <div className="border-t border-[#27272a] pt-8">
              <div className="label-sm text-[#f59e0b] mb-5">Secondary line</div>
              <a
                href={`tel:${BUSINESS.phone2Raw}`}
                className="group inline-flex flex-col"
                data-testid="contact-phone-2"
              >
                <div className="font-display font-black text-4xl sm:text-5xl md:text-[5rem] uppercase leading-[0.85] tracking-tight group-hover:text-[#f59e0b] transition-colors">
                  {BUSINESS.phone2}
                </div>
                <div className="flex items-center gap-3 mt-4 text-xs uppercase tracking-[0.3em] text-[#a1a1aa]">
                  <Phone className="w-4 h-4 text-[#f59e0b]" strokeWidth={3} />
                  Alternate · Tap to call
                </div>
              </a>
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4 border-t border-[#27272a] pt-10">
              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                className="inline-flex items-center justify-center gap-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
                data-testid="contact-call-cta"
              >
                <Phone className="w-4 h-4" strokeWidth={3} />
                Call now
              </a>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
                data-testid="contact-services-cta"
              >
                See services
                <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
              </Link>
            </div>

            {/* Hours card */}
            <div
              className="bg-[#18181b] border border-[#27272a] p-6 md:p-8"
              data-testid="contact-hours"
            >
              <div className="flex items-center gap-3 mb-5">
                <Clock3 className="w-5 h-5 text-[#f59e0b]" />
                <div className="label-sm text-[#f59e0b]">Hours</div>
              </div>
              <ul className="space-y-3 text-[#fafaf9]">
                <li className="flex items-center justify-between border-b border-[#27272a] pb-3">
                  <span>Monday – Friday</span>
                  <span className="font-bold">7:30 AM – 5:30 PM</span>
                </li>
                <li className="flex items-center justify-between border-b border-[#27272a] pb-3">
                  <span>Saturday</span>
                  <span className="font-bold">By appointment</span>
                </li>
                <li className="flex items-center justify-between">
                  <span>Sunday</span>
                  <span className="text-[#a1a1aa]">Closed</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right: Map + cities */}
          <div className="lg:col-span-5 space-y-5">
            <div
              className="bg-[#18181b] border border-[#27272a] p-5 md:p-6 relative"
              data-testid="contact-map"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="label-sm text-[#f59e0b]">Coverage</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa]">
                  AB · SK · MB
                </div>
              </div>
              <PrairieMap className="w-full h-auto" />
            </div>

            <div
              className="bg-[#18181b] border border-[#27272a] p-6 md:p-8"
              data-testid="contact-areas"
            >
              <div className="flex items-center gap-3 mb-5">
                <MapPin className="w-5 h-5 text-[#f59e0b]" />
                <div className="label-sm text-[#f59e0b]">Service cities</div>
              </div>
              <ul className="divide-y divide-[#27272a]">
                {SERVICE_AREAS.map((a, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-3 py-3.5 first:pt-0 last:pb-0"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-display font-black text-[#52525b] text-xs w-6">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <div className="font-display font-bold text-lg md:text-xl uppercase leading-tight">
                          {a.city}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] mt-0.5">
                          {a.region}
                        </div>
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] font-bold border border-[#27272a] px-2 py-1">
                      {a.province}
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/service-area"
                data-testid="contact-areas-link"
                className="mt-6 flex items-center justify-between pt-4 border-t border-[#27272a] text-[10px] uppercase tracking-[0.3em] font-bold text-[#a1a1aa] hover:text-[#f59e0b] transition-colors"
              >
                <span>View full coverage</span>
                <ArrowUpRight className="w-4 h-4 text-[#f59e0b]" strokeWidth={3} />
              </Link>
            </div>

            <div
              className="bg-[#f59e0b] text-[#09090b] p-6 md:p-8 relative overflow-hidden"
              data-testid="contact-cta-card"
            >
              <div className="absolute -top-8 -right-8 w-40 h-40 border-[3px] border-[#09090b]/20 pointer-events-none" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-3">
                  Fast response
                </div>
                <div className="font-display font-black text-3xl md:text-4xl uppercase leading-tight mb-5">
                  Prefer to talk?
                </div>
                <div className="flex flex-col gap-2.5">
                  <a
                    href={`tel:${BUSINESS.phoneRaw}`}
                    className="inline-flex items-center gap-3 bg-[#09090b] hover:bg-[#18181b] text-[#fafaf9] font-bold px-6 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
                    data-testid="contact-cta-call"
                  >
                    <Phone className="w-4 h-4" strokeWidth={3} />
                    {BUSINESS.phone}
                  </a>
                  <a
                    href={`tel:${BUSINESS.phone2Raw}`}
                    className="inline-flex items-center gap-3 bg-[#09090b] hover:bg-[#18181b] text-[#fafaf9] font-bold px-6 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
                    data-testid="contact-cta-call-2"
                  >
                    <Phone className="w-4 h-4" strokeWidth={3} />
                    {BUSINESS.phone2}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promise band */}
      <section
        className="py-20 md:py-24 bg-[#050505] border-y border-[#27272a]"
        data-testid="contact-promise"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 text-center">
          <div className="label-sm text-[#f59e0b] mb-6">Our promise</div>
          <div className="font-display font-black text-3xl md:text-5xl uppercase leading-[0.95] max-w-4xl mx-auto">
            No pressure. No surprises.
            <br />
            <span className="text-[#f59e0b]">Just honest, durable work.</span>
          </div>
        </div>
      </section>
    </div>
  );
}
